import { supabase } from "Infrastructure/Supabase/supabaseClient";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { REGION_TRANSPORT_KEY } from "Market/Api/Query/useRegionTransport";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MARKET_REGION_LIST } from "../Query/useMarketRegionList";

const marketMutationsResource = Resources.base.market.mutations;

export const useUpdateRegionTransportMutation = (
	marketId: number,
	regionCode: string | null | undefined,
) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: { transport: string }) => {
			const { data: updated, error } = await supabase
				.from("cities")
				.update({ transport: data.transport })
				.eq("city_code", regionCode)
				.select();

			if (error) throw error;

			if (!updated || updated.length === 0) {
				throw new Error("CITY_NOT_FOUND");
			}
		},
		onError: (error) => {
			if (error.message === "CITY_NOT_FOUND") {
				toast.warning("Create a city in Supabase");
			} else {
				console.error(
					`${t(marketMutationsResource.updateRegionError)}:`,
					error,
				);
			}
		},
		onSuccess: () => {
			toast.success(t(marketMutationsResource.updateRegionSuccess));
			queryClient.invalidateQueries({
				queryKey: [MARKET_REGION_LIST, marketId],
			});
			queryClient.invalidateQueries({
				queryKey: [REGION_TRANSPORT_KEY, regionCode],
			});
		},
	});
};

export const useDeleteRegionTransportMutation = (
	marketId: number,
	regionCode: string | null | undefined,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!regionCode) {
				toast.error("Region code is required");
				throw new Error("REGION_CODE_NOT_FOUND");
			}

			const { error } = await supabase
				.from("cities")
				.delete()
				.eq("city_code", regionCode);

			if (error) {
				throw error;
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [MARKET_REGION_LIST, marketId],
			});
			queryClient.invalidateQueries({
				queryKey: [REGION_TRANSPORT_KEY, regionCode],
			});
		},
	});
};
